function renderFormContentTemplate({
  slugValueHelperName,
  slugFallbackValue
}: {
  slugValueHelperName: string
  slugFallbackValue: string
}): string {
  return `
function ${slugValueHelperName}(value: unknown): string {
  if (typeof value !== 'string') return '${slugFallbackValue}'
  return slugify(value) || '${slugFallbackValue}'
}
`
}

function renderFormTabContentTemplate({
  indent,
  tName,
  tabFields
}: {
  indent: string
  tName: string
  tabFields: string
}): string {
  return `${indent}  <TabsContent value="${tName}" className="space-y-4 pt-4 pb-6 px-4 rounded-sm border bg-card">
${tabFields}
${indent}  </TabsContent>`
}

function renderFormTabsLayoutTemplate({
  indent,
  tabsList,
  tabsContent
}: {
  indent: string
  tabsList: string
  tabsContent: string
}): string {
  return `${indent}<Tabs value={activeTab} onValueChange={(value) => void setActiveTab(value as TabValue)} className="w-full">
${indent}  <TabsList>
${tabsList}
${indent}  </TabsList>
${tabsContent}
${indent}</Tabs>`
}

function renderFormContentSerializedValueTemplate({
  cardFieldIndent,
  stringify,
  fieldJsx
}: {
  cardFieldIndent: string
  stringify: string
  fieldJsx: string
}): string {
  return `${cardFieldIndent}{activeTab === ${stringify} ? (
${fieldJsx}
${cardFieldIndent}) : null}`
}

function renderFormContentSerializedValueBranchTemplate({
  cardFieldIndent,
  stringify,
  fieldJsx
}: {
  cardFieldIndent: string
  stringify: string
  fieldJsx: string
}): string {
  return `${cardFieldIndent}{activeTab === ${stringify} ? (
${cardFieldIndent}  <>
${fieldJsx}
${cardFieldIndent}  </>
${cardFieldIndent}) : null}`
}

function renderFormLayoutTemplate({
  indent,
  iconName
}: {
  indent: string
  iconName: string
}): string {
  return `
${indent}    <${iconName} className="size-4 shrink-0" />`
}

function renderFormCardHeaderTemplate({
  indent,
  iconJsx,
  stringify
}: {
  indent: string
  iconJsx: string
  stringify: string
}): string {
  return `${indent}  <CardHeader>
${indent}    <CardTitle className="flex items-center gap-1.5">${iconJsx}
${indent}      <span>{${stringify}}</span>
${indent}    </CardTitle>
${indent}  </CardHeader>`
}

function renderFormCardContentTemplate({
  indent,
  headingJsx,
  fieldsJsx
}: {
  indent: string
  headingJsx: string
  fieldsJsx: string
}): string {
  return `${indent}<Card className="gap-0">
${headingJsx}
${indent}  <CardContent className="space-y-5 p-5">
${fieldsJsx}
${indent}  </CardContent>
${indent}</Card>`
}

function renderFormCardLayoutTemplate({
  indent,
  fieldsJsx
}: {
  indent: string
  fieldsJsx: string
}): string {
  return `${indent}<Card className="space-y-4 p-5 gap-0">
${fieldsJsx}
${indent}</Card>`
}

function renderFormContentSerializedValueBranchBodyTemplate({
  stringify,
  cardJsx
}: {
  stringify: string
  cardJsx: string
}): string {
  return `              {activeTab === ${stringify} ? (
${cardJsx}
              ) : null}`
}

function renderFormContentClassNameTemplate({
  optionsScrollableClassName,
  join
}: {
  optionsScrollableClassName: string
  join: string
}): string {
  return `              <div ${optionsScrollableClassName}>
${join}
              </div>`
}

function renderFormLayoutDetailsTemplate(): string {
  return `className={initialData?.id != null
                  ? 'sticky top-32 space-y-4 pb-4'
                  : 'sticky top-14 space-y-4 pb-4'}`
}

function renderFormCardContentGridTemplate({
  tabSidebarEntriesGrid,
  tabMainFieldsJsx,
  sidebarColumn
}: {
  tabSidebarEntriesGrid: string
  tabMainFieldsJsx: string
  sidebarColumn: string
}): string {
  return `      <div className="grid ${tabSidebarEntriesGrid} h-full min-h-0 gap-4 items-stretch">
        <div className="h-full min-h-0 min-w-0">
          <Card className="min-h-0 min-w-0 h-full">
            <CardContent className="space-y-5 gap-0 h-full min-h-0">
${tabMainFieldsJsx}
            </CardContent>
          </Card>
        </div>
${sidebarColumn}
      </div>`
}

function renderFormLayoutDetailsBranchTemplate(): string {
  return `            <div
              className={cn(
                'grid grid-cols-1 gap-4 items-start h-full',
                {
                  'grid-cols-[1fr_360px]': hasVisibleSidebarFields
                }
              )}
            >`
}

function renderFormContentSidebarColumnTemplate({
  renderSidebarColumn
}: {
  renderSidebarColumn: string
}): string {
  return `              {hasVisibleSidebarFields ? (
${renderSidebarColumn}
              ) : null}`
}

function renderFormCardContentDetailsTemplate({
  formGridOpen,
  mainFieldsJsx,
  sidebarCardJsx
}: {
  formGridOpen: string
  mainFieldsJsx: string
  sidebarCardJsx: string
}): string {
  return `${formGridOpen}
              <div className="h-full min-w-0">
                <Card className="min-w-0 h-full">
                <CardContent className="space-y-5 gap-0 h-full">
${mainFieldsJsx}
</CardContent>
                </Card>
              </div>
${sidebarCardJsx}
            </div>`
}

function renderFormContentLucideTemplate({
  lucideIconsLucide,
  hasTabsFieldHookImportPath,
  needsCnImport,
  join
}: {
  lucideIconsLucide: string
  hasTabsFieldHookImportPath: string
  needsCnImport: string
  join: string
}): string {
  return `${lucideIconsLucide}${hasTabsFieldHookImportPath}${needsCnImport}
${join}`
}

function renderFormGeneratedValueTemplate({ map }: { map: string }): string {
  return `  const {
${map}
  } = state

`
}

function renderFormClientModuleTemplate({
  tabImports,
  specComponentName,
  hookName,
  tabStateDestructure,
  body
}: {
  tabImports: string
  specComponentName: string
  hookName: string
  tabStateDestructure: string
  body: string
}): string {
  return `'use client'

${tabImports}

interface ${specComponentName}Props {
  state: ReturnType<typeof ${hookName}>
}

export function ${specComponentName}({ state }: ${specComponentName}Props) {
${tabStateDestructure}  return (
${body}
  )
}
`
}

function renderEntityFormContent({
  contentImports,
  contentComponentName,
  hookName,
  topLevelTabsFieldTab,
  topLevelTabsFieldTabTab,
  contentStateDestructure,
  contentSidebarVisibilityState,
  schemaName,
  hasDraftInitialData,
  topLevelTabsFieldClassName,
  formBodyJsx
}: {
  contentImports: string
  contentComponentName: string
  hookName: string
  topLevelTabsFieldTab: string
  topLevelTabsFieldTabTab: string
  contentStateDestructure: string
  contentSidebarVisibilityState: string
  schemaName: string
  hasDraftInitialData: string
  topLevelTabsFieldClassName: string
  formBodyJsx: string
}): string {
  return `'use client'

${contentImports}

interface ${contentComponentName}Props {
  state: ReturnType<typeof ${hookName}>
${topLevelTabsFieldTab}
}

export function ${contentComponentName}({ state${topLevelTabsFieldTabTab} }: ${contentComponentName}Props) {
  const {
${contentStateDestructure}
  } = state
${contentSidebarVisibilityState}
  return (
    <Form {...form}>
      <form autoComplete="off" id="${schemaName}-form" onSubmit={form.handleSubmit((values) => onSubmit(values${hasDraftInitialData}), handleErrors)} ${topLevelTabsFieldClassName}>
      ${formBodyJsx}
      </form>
    </Form>
  )
}
`
}

export function renderEntityFormContentRenderer(
  request:
    | {
        kind: 'renderFormContentTemplate'
        props: {
          slugValueHelperName: string
          slugFallbackValue: string
        }
      }
    | {
        kind: 'renderFormTabContentTemplate'
        props: {
          indent: string
          tName: string
          tabFields: string
        }
      }
    | {
        kind: 'renderFormTabsLayoutTemplate'
        props: {
          indent: string
          tabsList: string
          tabsContent: string
        }
      }
    | {
        kind: 'renderFormContentSerializedValueTemplate'
        props: {
          cardFieldIndent: string
          stringify: string
          fieldJsx: string
        }
      }
    | {
        kind: 'renderFormContentSerializedValueBranchTemplate'
        props: {
          cardFieldIndent: string
          stringify: string
          fieldJsx: string
        }
      }
    | {
        kind: 'renderFormLayoutTemplate'
        props: {
          indent: string
          iconName: string
        }
      }
    | {
        kind: 'renderFormCardHeaderTemplate'
        props: {
          indent: string
          iconJsx: string
          stringify: string
        }
      }
    | {
        kind: 'renderFormCardContentTemplate'
        props: {
          indent: string
          headingJsx: string
          fieldsJsx: string
        }
      }
    | {
        kind: 'renderFormCardLayoutTemplate'
        props: {
          indent: string
          fieldsJsx: string
        }
      }
    | {
        kind: 'renderFormContentSerializedValueBranchBodyTemplate'
        props: {
          stringify: string
          cardJsx: string
        }
      }
    | {
        kind: 'renderFormContentClassNameTemplate'
        props: {
          optionsScrollableClassName: string
          join: string
        }
      }
    | { kind: 'renderFormLayoutDetailsTemplate' }
    | {
        kind: 'renderFormCardContentGridTemplate'
        props: {
          tabSidebarEntriesGrid: string
          tabMainFieldsJsx: string
          sidebarColumn: string
        }
      }
    | { kind: 'renderFormLayoutDetailsBranchTemplate' }
    | {
        kind: 'renderFormContentSidebarColumnTemplate'
        props: {
          renderSidebarColumn: string
        }
      }
    | {
        kind: 'renderFormCardContentDetailsTemplate'
        props: {
          formGridOpen: string
          mainFieldsJsx: string
          sidebarCardJsx: string
        }
      }
    | {
        kind: 'renderFormContentLucideTemplate'
        props: {
          lucideIconsLucide: string
          hasTabsFieldHookImportPath: string
          needsCnImport: string
          join: string
        }
      }
    | { kind: 'renderFormGeneratedValueTemplate'; props: { map: string } }
    | {
        kind: 'renderFormClientModuleTemplate'
        props: {
          tabImports: string
          specComponentName: string
          hookName: string
          tabStateDestructure: string
          body: string
        }
      }
    | {
        kind: 'renderEntityFormContent'
        props: {
          contentImports: string
          contentComponentName: string
          hookName: string
          topLevelTabsFieldTab: string
          topLevelTabsFieldTabTab: string
          contentStateDestructure: string
          contentSidebarVisibilityState: string
          schemaName: string
          hasDraftInitialData: string
          topLevelTabsFieldClassName: string
          formBodyJsx: string
        }
      }
): string {
  switch (request.kind) {
    case 'renderFormContentTemplate':
      return renderFormContentTemplate(request.props)
    case 'renderFormTabContentTemplate':
      return renderFormTabContentTemplate(request.props)
    case 'renderFormTabsLayoutTemplate':
      return renderFormTabsLayoutTemplate(request.props)
    case 'renderFormContentSerializedValueTemplate':
      return renderFormContentSerializedValueTemplate(request.props)
    case 'renderFormContentSerializedValueBranchTemplate':
      return renderFormContentSerializedValueBranchTemplate(request.props)
    case 'renderFormLayoutTemplate':
      return renderFormLayoutTemplate(request.props)
    case 'renderFormCardHeaderTemplate':
      return renderFormCardHeaderTemplate(request.props)
    case 'renderFormCardContentTemplate':
      return renderFormCardContentTemplate(request.props)
    case 'renderFormCardLayoutTemplate':
      return renderFormCardLayoutTemplate(request.props)
    case 'renderFormContentSerializedValueBranchBodyTemplate':
      return renderFormContentSerializedValueBranchBodyTemplate(request.props)
    case 'renderFormContentClassNameTemplate':
      return renderFormContentClassNameTemplate(request.props)
    case 'renderFormLayoutDetailsTemplate':
      return renderFormLayoutDetailsTemplate()
    case 'renderFormCardContentGridTemplate':
      return renderFormCardContentGridTemplate(request.props)
    case 'renderFormLayoutDetailsBranchTemplate':
      return renderFormLayoutDetailsBranchTemplate()
    case 'renderFormContentSidebarColumnTemplate':
      return renderFormContentSidebarColumnTemplate(request.props)
    case 'renderFormCardContentDetailsTemplate':
      return renderFormCardContentDetailsTemplate(request.props)
    case 'renderFormContentLucideTemplate':
      return renderFormContentLucideTemplate(request.props)
    case 'renderFormGeneratedValueTemplate':
      return renderFormGeneratedValueTemplate(request.props)
    case 'renderFormClientModuleTemplate':
      return renderFormClientModuleTemplate(request.props)
    case 'renderEntityFormContent':
      return renderEntityFormContent(request.props)
  }

  throw new Error('Unsupported renderer request')
}
